import { useCompanySearch } from '@/queries/company_searches'
import { CompanySearchResult, SharedProps } from '@/types'
import { usePage } from '@inertiajs/react'
import { Badge, Group, Loader, Paper, Stack, Text, TextInput, UnstyledButton } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconBuilding, IconSearch } from '@tabler/icons-react'
import { ChangeEvent, KeyboardEvent, useState } from 'react'

function CompanySearchResultCard({
  company,
  onSelect,
}: {
  company: CompanySearchResult
  onSelect: (company: CompanySearchResult) => void
}) {
  const handleClick = () => onSelect(company)
  const address = [company.street_address, company.postal_code, company.city].filter(Boolean).join(', ')

  return (
    <UnstyledButton onClick={handleClick} w="100%">
      <Paper withBorder p="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <IconBuilding size={18} color="var(--mantine-color-gray-6)" />
            <div>
              <Text size="sm" fw={600}>
                {company.legal_name} · SIREN {company.siren}
              </Text>
              <Text size="xs" c="dimmed">
                {address}
              </Text>
            </div>
          </Group>
          <Badge variant="light" color={company.active ? 'green' : 'gray'}>
            {company.active ? 'Active' : 'Fermée'}
          </Badge>
        </Group>
      </Paper>
    </UnstyledButton>
  )
}

export default function CompanySearch({ onSelect }: { onSelect: (company: CompanySearchResult) => void }) {
  const { current_organization } = usePage<SharedProps>().props
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebouncedValue(query, 400)
  const { data: companies, isFetching } = useCompanySearch({
    organizationSlug: current_organization!.slug,
    query: debouncedQuery,
  })

  const handleQueryChange = (event: ChangeEvent<HTMLInputElement>) => setQuery(event.currentTarget.value)

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => event.key === 'Enter' && event.preventDefault()

  const handleSelect = (company: CompanySearchResult) => {
    onSelect(company)
    setQuery('')
  }

  const showResults = query.trim().length >= 3 && companies

  return (
    <Stack gap={6}>
      <TextInput
        label="Rechercher la société"
        description="Nom, SIREN, SIRET ou n° de TVA, dans le registre public des entreprises"
        placeholder="Ex : Hôtel du Canal, 552 100 554…"
        leftSection={<IconSearch size={16} />}
        rightSection={isFetching ? <Loader size="xs" /> : null}
        value={query}
        onChange={handleQueryChange}
        onKeyDown={handleKeyDown}
      />
      {showResults && companies.length === 0 && (
        <Text size="sm" c="dimmed">
          Aucune société trouvée. Essayez avec le SIREN.
        </Text>
      )}
      {showResults &&
        companies.map((company) => (
          <CompanySearchResultCard key={company.siren} company={company} onSelect={handleSelect} />
        ))}
    </Stack>
  )
}
